// src/pages/Subscriptions.js
//
// SUIVI DES PAIEMENTS — v2 (refonte)
//
// Ce qui change par rapport à la v1 :
//  • UNE LIGNE PAR ÉLÈVE (ou fratrie) : les inscriptions d'un même élève
//    (site + app, abonnement + carte 10, saisons successives…) sont regroupées
//    par identité (studentId OU prénom + nom normalisés) — fini les doublons.
//  • L'échéancier est calculé pour TOUS les abonnements, y compris ceux payés
//    par virement / espèces (avant : seuls les champs écrits par Mollie
//    étaient affichés, d'où les colonnes vides).
//  • « Reste à payer », « En retard » et prochaine échéance (date + montant)
//    pour chaque élève.
//  • Nouvel onglet « À encaisser » : toutes les échéances dues ou proches,
//    tous élèves confondus, avec bouton « Encaisser » direct.
//  • Clic sur un élève → fiche de suivi (échéancier, encaissements,
//    transactions Mollie, présences rattachées à chaque formule).
//
// Logique de calcul : src/utils/billing.js (partagée avec Registrations.js
// et AttendancePage.js). Écritures : src/utils/billingActions.js.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, FormControl, InputLabel, Select,
  Chip, LinearProgress, CircularProgress, Grid, AppBar, Toolbar, IconButton,
  ToggleButton, ToggleButtonGroup, Tooltip, Button, Snackbar, Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  EventRepeat as EventRepeatIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Payments as PayIcon,
} from '@mui/icons-material';
import {
  computeBilling, registrationKind, registrationStartDate, groupRegistrationsByStudent,
  seasonOf, currentSeason, cardCredits, billingStatusMeta, worstStatus,
  INSTALLMENT_STATUS, FREQUENCY_LABELS, PAYMENT_MODE_LABELS, formatDay, money,
  planLabel, readDate, round2, todayStart, addDays, normalizeText, DUE_SOON_DAYS,
} from '../utils/billing';
import StudentBillingDialog from '../components/billing/StudentBillingDialog';
import RecordPaymentDialog from '../components/billing/RecordPaymentDialog';

const PURPLE = '#7C3AED';

const StatusChip = ({ status, suffix }) => {
  const meta = billingStatusMeta(status);
  return (
    <Chip
      label={suffix ? `${meta.label} ${suffix}` : meta.label}
      size="small"
      sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600 }}
    />
  );
};

const Progress = ({ value, color = PURPLE }) => (
  <LinearProgress
    variant="determinate" value={Math.max(0, Math.min(value, 100))}
    sx={{ mt: 0.5, height: 6, borderRadius: 4, backgroundColor: '#eee', '& .MuiLinearProgress-bar': { backgroundColor: color } }}
  />
);

const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));

function Subscriptions() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading]             = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [paymentsByReg, setPaymentsByReg] = useState({});
  const [paymentsError, setPaymentsError] = useState(false);

  const [tab, setTab]                   = useState('subscriptions'); // 'subscriptions' | 'cards' | 'collect'
  const [search, setSearch]             = useState('');
  const [season, setSeason]             = useState(currentSeason());
  const [statusFilter, setStatusFilter] = useState('all');
  const [horizon, setHorizon]           = useState('30');           // 'late' | '30' | 'all'

  const [dialogRegId, setDialogRegId] = useState(null);
  const [payTarget, setPayTarget]     = useState(null);
  const [snackbar, setSnackbar]       = useState({ open: false, message: '', severity: 'success' });

  // ── Chargement ───────────────────────────────────────────────────────────

  const fetchAll = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const regSnap = await getDocs(collection(db, 'registrations'));
      const regs = regSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.status !== 'cancelled');

      const byReg = {};
      try {
        const paySnap = await getDocs(collection(db, 'payments'));
        paySnap.forEach((p) => {
          const data = p.data();
          const ref = data.refPath || '';
          if (!ref.startsWith('registrations/')) return;
          const regId = ref.slice('registrations/'.length);
          if (!byReg[regId]) byReg[regId] = [];
          byReg[regId].push({ id: p.id, ...data });
        });
        setPaymentsError(false);
      } catch (e) {
        // Sans la collection payments, on se rabat sur installmentsPaid (compteur Mollie)
        console.warn('Subscriptions : lecture de payments impossible', e);
        setPaymentsError(true);
      }

      setRegistrations(regs);
      setPaymentsByReg(byReg);
    } catch (e) {
      console.error('Erreur chargement abonnements:', e);
      setSnackbar({ open: true, message: 'Erreur de chargement des inscriptions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Arrivée depuis Registrations.js (« Suivi des paiements ») : ouvrir la fiche
  useEffect(() => {
    const targetId = location.state?.openRegistrationId;
    if (!targetId || registrations.length === 0) return;
    if (registrations.some((r) => r.id === targetId)) setDialogRegId(targetId);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, location.state]);

  // ── Calculs ──────────────────────────────────────────────────────────────

  const enriched = useMemo(() => registrations.map((r) => ({
    ...r,
    _billing: computeBilling(r, paymentsByReg[r.id] || []),
    _kind: registrationKind(r),
    _season: seasonOf(registrationStartDate(r)),
  })), [registrations, paymentsByReg]);

  const groups = useMemo(() => groupRegistrationsByStudent(enriched), [enriched]);

  const groupOfReg = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => g.registrations.forEach((r) => m.set(r.id, g)));
    return m;
  }, [groups]);

  const seasons = useMemo(
    () => unique(enriched.map((r) => r._season)).sort().reverse(),
    [enriched]
  );

  const inSeason = useCallback((r) => season === 'all' || r._season === season, [season]);

  const matchesSearch = useCallback((g) => {
    const s = normalizeText(search);
    if (!s) return true;
    const hay = normalizeText([g.label, ...g.emails, ...g.phones, ...g.registrations.map(planLabel)].join(' '));
    return hay.includes(s);
  }, [search]);

  // Onglet Abonnements : une ligne par élève
  const subscriptionRows = useMemo(() => groups.map((g) => {
    const regs = g.registrations.filter((r) => r._kind === 'subscription' && inSeason(r));
    if (regs.length === 0) return null;
    const unpaid = regs
      .flatMap((r) => r._billing.installments.filter((i) => i.status !== 'paid').map((i) => ({ ...i, reg: r })))
      .sort((a, b) => a.dueDate - b.dueDate);
    const sum = (f) => round2(regs.reduce((acc, r) => acc + f(r._billing), 0));
    return {
      group: g,
      regs,
      planNames: unique(regs.map(planLabel)),
      frequencies: unique(regs.map((r) => (r._billing.recurring ? (FREQUENCY_LABELS[r.paymentFrequency] || 'Échelonné') : (FREQUENCY_LABELS[r.paymentFrequency] || 'Paiement unique')))),
      modes: unique(regs.map((r) => PAYMENT_MODE_LABELS[r.paymentMode] || (r.paymentMode ? r.paymentMode : 'Manuel'))),
      start: regs.map((r) => registrationStartDate(r)).filter(Boolean).sort((a, b) => a - b)[0] || null,
      paidCount: sum((b) => b.paidCount),
      total: sum((b) => b.total),
      expected: sum((b) => b.expected),
      paid: sum((b) => b.paid),
      remaining: sum((b) => b.remaining),
      overdueAmount: sum((b) => b.overdueAmount),
      next: unpaid[0] || null,
      status: worstStatus(regs.map((r) => r._billing.status)),
      amountEstimated: regs.some((r) => r._billing.amountEstimated || r._billing.amountUnknown),
    };
  }).filter(Boolean), [groups, inSeason]);

  const filteredSubs = useMemo(() => {
    const soon = addDays(todayStart(), DUE_SOON_DAYS);
    return subscriptionRows
      .filter((r) => matchesSearch(r.group))
      .filter((r) => {
        switch (statusFilter) {
          case 'late':    return r.status === 'overdue' || r.status === 'failed';
          case 'dueSoon': return !!r.next && r.next.dueDate <= soon;
          case 'failed':  return r.status === 'failed';
          case 'ok':      return ['up_to_date', 'processing', 'due', 'pending_mandate'].includes(r.status);
          case 'paid':    return r.status === 'paid';
          case 'check':   return r.amountEstimated;
          default:        return true;
        }
      })
      .sort((a, b) => {
        const ra = billingStatusMeta(a.status).rank;
        const rb = billingStatusMeta(b.status).rank;
        if (ra >= 6 || rb >= 6) { if (ra !== rb) return rb - ra; }
        const an = a.next ? a.next.dueDate.getTime() : Infinity;
        const bn = b.next ? b.next.dueDate.getTime() : Infinity;
        if (an !== bn) return an - bn;
        return a.group.label.localeCompare(b.group.label);
      });
  }, [subscriptionRows, statusFilter, matchesSearch]);

  // Onglet Cartes 10 : une ligne par élève, carte en cours mise en avant
  const cardRows = useMemo(() => groups.map((g) => {
    const cards = g.registrations
      .filter((r) => r._kind === 'card' && inSeason(r))
      .sort((a, b) => (readDate(b.createdAt)?.getTime() || 0) - (readDate(a.createdAt)?.getTime() || 0));
    if (cards.length === 0) return null;
    const current = cards.find((c) => cardCredits(c).remaining > 0) || cards[0];
    const credits = cardCredits(current);
    const lastUse = cards
      .flatMap((c) => cardCredits(c).consumed.map((cl) => cl.dateKey))
      .filter(Boolean).sort().pop() || null;
    return {
      group: g,
      cards,
      current,
      credits,
      unpaid: round2(cards.reduce((acc, c) => acc + c._billing.remaining, 0)),
      status: worstStatus(cards.map((c) => c._billing.status)),
      lastUse,
      hasSubscription: g.registrations.some((r) => r._kind === 'subscription' && inSeason(r)),
    };
  }).filter(Boolean), [groups, inSeason]);

  const filteredCards = useMemo(() => cardRows
    .filter((r) => matchesSearch(r.group))
    .sort((a, b) => a.credits.remaining - b.credits.remaining || a.group.label.localeCompare(b.group.label)),
  [cardRows, matchesSearch]);

  // Onglet À encaisser : échéances non payées, tous élèves confondus.
  // « En retard » et « 30 jours » ignorent la saison (une dette de l'an
  // dernier reste à encaisser) ; « Toutes » suit le filtre saison.
  const collectRows = useMemo(() => {
    const limit = addDays(todayStart(), 30);
    return enriched
      .filter((r) => r._kind !== 'trial')
      .filter((r) => horizon !== 'all' || inSeason(r))
      .flatMap((r) => r._billing.installments
        .filter((i) => i.status !== 'paid')
        .filter((i) => {
          if (horizon === 'late') return i.status === 'overdue' || i.status === 'failed';
          if (horizon === '30')   return i.dueDate <= limit;
          return true;
        })
        .map((i) => ({ reg: r, inst: i, group: groupOfReg.get(r.id) })))
      .filter((row) => row.group && matchesSearch(row.group))
      .sort((a, b) => a.inst.dueDate - b.inst.dueDate);
  }, [enriched, horizon, inSeason, groupOfReg, matchesSearch]);

  // Indicateurs (saison sélectionnée, hors cours d'essai)
  const kpis = useMemo(() => {
    const regs = enriched.filter((r) => r._kind !== 'trial' && inSeason(r));
    const limit = addDays(todayStart(), 30);
    const today = todayStart();
    const allLate = enriched.filter((r) => r._kind !== 'trial' && r._billing.overdueAmount > 0);
    return {
      paid: round2(regs.reduce((a, r) => a + r._billing.paid, 0)),
      remaining: round2(regs.reduce((a, r) => a + r._billing.remaining, 0)),
      late: round2(allLate.reduce((a, r) => a + r._billing.overdueAmount, 0)),
      lateStudents: new Set(allLate.map((r) => groupOfReg.get(r.id)?.key)).size,
      next30: round2(enriched
        .filter((r) => r._kind !== 'trial')
        .flatMap((r) => r._billing.installments)
        .filter((i) => i.status !== 'paid' && i.status !== 'overdue' && i.status !== 'failed' && i.dueDate >= addDays(today, -7) && i.dueDate <= limit)
        .reduce((a, i) => a + i.remaining, 0)),
    };
  }, [enriched, inSeason, groupOfReg]);

  const dialogGroup = dialogRegId ? groupOfReg.get(dialogRegId) || null : null;

  const handleChanged = async (message) => {
    await fetchAll({ silent: true });
    if (message) setSnackbar({ open: true, message, severity: 'success' });
  };

  const openGroup = (g) => setDialogRegId(g.registrations[0].id);

  // ── Rendu ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 0 }}>
      <AppBar position="static" elevation={0} sx={{ backgroundColor: PURPLE, mb: 3 }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <EventRepeatIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Suivi des paiements
          </Typography>
          {kpis.lateStudents > 0 && (
            <Tooltip title={`${kpis.lateStudents} élève(s) en retard de paiement`}>
              <Chip
                icon={<WarningIcon sx={{ color: '#fff !important' }} />}
                label={kpis.lateStudents}
                size="small"
                onClick={() => { setTab('collect'); setHorizon('late'); }}
                sx={{ backgroundColor: '#c62828', color: '#fff', fontWeight: 700, mr: 1 }}
              />
            </Tooltip>
          )}
          <Tooltip title="Actualiser">
            <IconButton color="inherit" onClick={() => fetchAll()}><RefreshIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Indicateurs */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: season === 'all' ? 'Encaissé (toutes saisons)' : `Encaissé ${season}`, value: money(kpis.paid), color: '#2e7d32' },
            { label: 'Reste à percevoir', value: money(kpis.remaining), color: 'inherit' },
            { label: 'Échéances des 30 prochains jours', value: money(kpis.next30), color: '#1565c0', onClick: () => { setTab('collect'); setHorizon('30'); } },
            { label: `En retard (${kpis.lateStudents} élève${kpis.lateStudents > 1 ? 's' : ''})`, value: money(kpis.late), color: kpis.late > 0 ? '#c62828' : 'inherit', onClick: () => { setTab('collect'); setHorizon('late'); } },
          ].map((k) => (
            <Grid item xs={12} sm={6} md={3} key={k.label}>
              <Paper
                sx={{ p: 2, cursor: k.onClick ? 'pointer' : 'default', '&:hover': k.onClick ? { boxShadow: 3 } : {} }}
                onClick={k.onClick}
              >
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: k.color }}>{loading ? '…' : k.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Filtres */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup exclusive value={tab} onChange={(e, val) => { if (val) setTab(val); }} size="small">
            <ToggleButton value="subscriptions" sx={{ px: 2 }}>Abonnements ({subscriptionRows.length})</ToggleButton>
            <ToggleButton value="cards" sx={{ px: 2 }}>Cartes 10 cours ({cardRows.length})</ToggleButton>
            <ToggleButton value="collect" sx={{ px: 2 }}>À encaisser</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small" placeholder="Nom, e-mail, téléphone…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Saison</InputLabel>
            <Select label="Saison" value={season} onChange={(e) => setSeason(e.target.value)}>
              {unique([currentSeason(), ...seasons]).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              <MenuItem value="all">Toutes</MenuItem>
            </Select>
          </FormControl>

          {tab === 'subscriptions' && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Statut</InputLabel>
              <Select label="Statut" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="late">En retard / échec</MenuItem>
                <MenuItem value="dueSoon">Échéance sous {DUE_SOON_DAYS} jours</MenuItem>
                <MenuItem value="failed">Prélèvement / mandat échoué</MenuItem>
                <MenuItem value="ok">À jour</MenuItem>
                <MenuItem value="paid">Soldés</MenuItem>
                <MenuItem value="check">Montant à vérifier</MenuItem>
              </Select>
            </FormControl>
          )}

          {tab === 'collect' && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Échéances</InputLabel>
              <Select label="Échéances" value={horizon} onChange={(e) => setHorizon(e.target.value)}>
                <MenuItem value="late">En retard uniquement</MenuItem>
                <MenuItem value="30">En retard + 30 prochains jours</MenuItem>
                <MenuItem value="all">Toutes les échéances restantes (saison)</MenuItem>
              </Select>
            </FormControl>
          )}
        </Paper>

        {paymentsError && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Les transactions Mollie n'ont pas pu être lues : le suivi se base sur le compteur
            d'échéances Mollie et les encaissements manuels.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : tab === 'subscriptions' ? (
          // ── Abonnements ──────────────────────────────────────────────────
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Élève</strong></TableCell>
                  <TableCell><strong>Formule</strong></TableCell>
                  <TableCell><strong>Échéances payées</strong></TableCell>
                  <TableCell><strong>Prochaine échéance</strong></TableCell>
                  <TableCell align="right"><strong>Reste à payer</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucun abonnement pour ces filtres.
                    </TableCell>
                  </TableRow>
                )}
                {filteredSubs.map((r) => {
                  const nextLate = r.next && (r.next.status === 'overdue' || r.next.status === 'failed');
                  return (
                    <TableRow key={r.group.key} hover sx={{ cursor: 'pointer' }} onClick={() => openGroup(r.group)}>
                      <TableCell>
                        <div style={{ fontWeight: 600 }}>{r.group.label}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{r.group.emails[0] || r.group.phones[0] || ''}</div>
                      </TableCell>
                      <TableCell>
                        <div>{r.planNames.join(' + ')}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {r.frequencies.join(', ')} · {r.modes.join(', ')} · depuis le {formatDay(r.start)}
                        </div>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <div>{r.paidCount}/{r.total} · {money(r.paid)} / {money(r.expected)}</div>
                        <Progress value={r.expected > 0 ? (r.paid / r.expected) * 100 : 0} />
                      </TableCell>
                      <TableCell sx={{ color: nextLate ? '#c62828' : 'inherit', fontWeight: nextLate ? 700 : 400 }}>
                        {r.next ? (
                          <>
                            {formatDay(r.next.dueDate)} · {money(r.next.remaining)}
                            <div style={{ fontSize: '0.75rem', color: nextLate ? '#c62828' : '#666', fontWeight: 400 }}>
                              {(INSTALLMENT_STATUS[r.next.status] || {}).label}
                              {r.next.overdueDays > 0 ? ` depuis ${r.next.overdueDays} j` : ''}
                            </div>
                          </>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <div style={{ fontWeight: 600 }}>{money(r.remaining)}</div>
                        {r.overdueAmount > 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#c62828' }}>dont {money(r.overdueAmount)} en retard</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={r.status} />
                        {r.amountEstimated && (
                          <Tooltip title="Montant par échéance non enregistré — à vérifier dans la fiche">
                            <Chip size="small" label="montant ?" variant="outlined" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : tab === 'cards' ? (
          // ── Cartes 10 cours ──────────────────────────────────────────────
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Élève</strong></TableCell>
                  <TableCell><strong>Carte en cours</strong></TableCell>
                  <TableCell><strong>Crédits utilisés</strong></TableCell>
                  <TableCell><strong>Restants</strong></TableCell>
                  <TableCell><strong>Dernier cours</strong></TableCell>
                  <TableCell><strong>Paiement</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCards.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucune carte 10 cours pour ces filtres.
                    </TableCell>
                  </TableRow>
                )}
                {filteredCards.map((r) => (
                  <TableRow key={r.group.key} hover sx={{ cursor: 'pointer' }} onClick={() => openGroup(r.group)}>
                    <TableCell>
                      <div style={{ fontWeight: 600 }}>{r.group.label}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        {r.group.emails[0] || ''}
                        {r.hasSubscription ? ' · a aussi un abonnement' : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      Achetée le {formatDay(r.current.createdAt)}
                      {r.cards.length > 1 && (
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{r.cards.length} cartes achetées</div>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <div>{r.credits.used}/{r.credits.total}</div>
                      <Progress
                        value={(r.credits.used / r.credits.total) * 100}
                        color={r.credits.remaining <= 2 ? '#e65100' : PURPLE}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.credits.remaining} size="small"
                        color={r.credits.remaining === 0 ? 'error' : r.credits.remaining <= 2 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>{r.lastUse ? formatDay(r.lastUse) : '—'}</TableCell>
                    <TableCell>
                      {r.unpaid > 0
                        ? <StatusChip status={r.status} suffix={`· ${money(r.unpaid)}`} />
                        : <StatusChip status="paid" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          // ── À encaisser ──────────────────────────────────────────────────
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Échéance</strong></TableCell>
                  <TableCell><strong>Élève</strong></TableCell>
                  <TableCell><strong>Formule</strong></TableCell>
                  <TableCell align="right"><strong>Montant dû</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {collectRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Rien à encaisser pour ces filtres. 🎉
                    </TableCell>
                  </TableRow>
                )}
                {collectRows.map(({ reg, inst, group }) => {
                  const st = INSTALLMENT_STATUS[inst.status] || INSTALLMENT_STATUS.upcoming;
                  const late = inst.status === 'overdue' || inst.status === 'failed';
                  return (
                    <TableRow key={`${reg.id}_${inst.number}`} hover sx={{ cursor: 'pointer' }} onClick={() => openGroup(group)}>
                      <TableCell sx={{ color: late ? '#c62828' : 'inherit', fontWeight: late ? 700 : 400 }}>
                        {formatDay(inst.dueDate)}
                        {inst.overdueDays > 0 && (
                          <div style={{ fontSize: '0.75rem', fontWeight: 400 }}>il y a {inst.overdueDays} j</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: 600 }}>{group.label}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{group.emails[0] || group.phones[0] || ''}</div>
                      </TableCell>
                      <TableCell>
                        {planLabel(reg)}
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {reg._billing.recurring ? `échéance ${inst.number}/${reg._billing.total}` : 'paiement unique'}
                          {' · '}{PAYMENT_MODE_LABELS[reg.paymentMode] || 'Manuel'}
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{money(inst.remaining)}</strong>
                        {inst.partial && (
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>sur {money(inst.amount)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={st.label} sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell align="right">
                        {inst.status !== 'processing' && (
                          <Button
                            size="small" variant="outlined" startIcon={<PayIcon />}
                            onClick={(e) => { e.stopPropagation(); setPayTarget({ reg, installment: inst, groupLabel: group.label }); }}
                            sx={{ borderColor: PURPLE, color: PURPLE, whiteSpace: 'nowrap' }}
                          >
                            Encaisser
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {collectRows.length > 0 && (
              <Box sx={{ p: 2, textAlign: 'right' }}>
                <Typography variant="body2">
                  Total : <strong>{money(collectRows.reduce((a, r) => a + r.inst.remaining, 0))}</strong>
                  {' '}sur {collectRows.length} échéance(s)
                </Typography>
              </Box>
            )}
          </TableContainer>
        )}
      </Box>

      <StudentBillingDialog
        open={!!dialogGroup}
        group={dialogGroup}
        paymentsByReg={paymentsByReg}
        onClose={() => setDialogRegId(null)}
        onChanged={handleChanged}
        onOpenRegistration={(regId) => navigate('/registrations', { state: { openRegistrationId: regId } })}
      />

      <RecordPaymentDialog
        open={!!payTarget}
        target={payTarget}
        onClose={() => setPayTarget(null)}
        onSaved={(msg) => { setPayTarget(null); handleChanged(msg); }}
      />

      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Subscriptions;